import TransparencyIcon from '@duyank/icons/external/Transparency';
import LockKeyIcon from '@duyank/icons/regular/LockKey';
import LockKeyOpenIcon from '@duyank/icons/regular/LockKeyOpen';
import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import Popover from '../common/popover/Popover';
import Slider from '../common/slider/Slider';
import { useEditor, useSelectedLayers } from '../hooks';
import { RootLayerProps } from '../layers';
import { isRootLayer } from '../ultils/layer/layers';
import SettingButton from './SettingButton';
import LayerSidebar from './sidebar/LayerSidebar';
import { createBreakpoint } from 'react-use';
import { defaultBreakpoints } from './default-breakpoints';
import { useTranslation } from 'react-i18next';

interface AspectRatio {
  ratio: number;
  baseWidth: number;
  baseHeight: number;
}

const CommonSettings = () => {
  const { t } = useTranslation();

  const transparencyButtonRef = useRef<HTMLDivElement>(null);
  const resizeButtonRef = useRef<HTMLDivElement>(null);
  const widthRef = useRef<HTMLInputElement>(null);
  const heightRef = useRef<HTMLInputElement>(null);
  const [openTransparencySetting, setOpenTransparencySetting] = useState(false);
  const [openResizeSetting, setOpenResizeSetting] = useState(false);
  const [lockSiteAspect, setLockSizeAspect] = useState(false);
  const { selectedLayers, selectedLayerIds } = useSelectedLayers();
  const { actions, activePage, sidebar, pageSize, isPageLocked } = useEditor(
    (state, query) => ({
      activePage: state.activePage,
      sidebar: state.sidebar,
      pageSize: query.getPageSize(),
      isPageLocked:
        state.pages[state.activePage] &&
        state.pages[state.activePage].layers.ROOT.data.locked,
    })
  );
  const useBreakpoint = createBreakpoint(defaultBreakpoints);
  const breakpoint = useBreakpoint();

  const [size, setSize] = useState(pageSize);
  const [aspectRatio, setAspectRatio] = useState<AspectRatio | null>(null);

  useEffect(() => {
    setSize(pageSize);
  }, [pageSize]);

  const isLocked = !!selectedLayers.find((l) => l.data.locked);
  const toggleLock = () => {
    if (isLocked) {
      actions.unlock(activePage, selectedLayerIds);
    } else {
      actions.lock(activePage, selectedLayerIds);
    }
  };

  const { transparency } = useMemo(() => {
    return Object.entries(selectedLayers).reduce(
      (acc, [, layer]) => {
        if (isRootLayer(layer)) {
          acc.transparency = Math.max(
            acc.transparency,
            typeof layer.data.props.image?.transparency !== 'undefined'
              ? layer.data.props.image.transparency
              : 1
          );
        } else {
          acc.transparency = Math.max(
            acc.transparency,
            typeof layer.data.props.transparency !== 'undefined'
              ? layer.data.props.transparency
              : 1
          );
        }
        return acc;
      },
      { transparency: 0 }
    );
  }, [selectedLayers]);

  const updateTransparency = (transparency: number) => {
    selectedLayerIds.forEach((layerId) => {
      if (layerId === 'ROOT') {
        actions.history
          .throttle(2000)
          .setProp<RootLayerProps>(activePage, layerId, {
            image: {
              transparency: transparency / 100,
            },
          });
      } else {
        actions.history.throttle(2000).setProp(activePage, layerId, {
          transparency: transparency / 100,
        });
      }
    });
  };
  useEffect(() => {
    setOpenTransparencySetting(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(selectedLayerIds)]);

  const toggleLockAspectRatio = () => {
    const width = parseInt((widthRef.current as HTMLInputElement).value);
    const height = parseInt((heightRef.current as HTMLInputElement).value);

    if (!lockSiteAspect && width > 0 && height > 0) {
      setAspectRatio({
        baseWidth: width,
        baseHeight: height,
        ratio: width / height,
      });
    }

    setLockSizeAspect(!lockSiteAspect);
  };

  const handleChangeSize = (value: string, type: 'width' | 'height') => {
    const v = parseInt(value, 10);
    if (type === 'width') {
      if (lockSiteAspect) {
        const { baseWidth, baseHeight } = aspectRatio;
        const newHeight = Math.round(baseHeight * v) / baseWidth;
        (heightRef.current as HTMLInputElement).value = String(newHeight);

        setSize({ height: newHeight, width: v });
      } else {
        setSize({ ...size, width: v });
      }
    }
    if (type === 'height') {
      if (lockSiteAspect) {
        const { baseWidth, baseHeight } = aspectRatio;
        const newWidth = Math.round((baseHeight * v) / baseWidth);
        (widthRef.current as HTMLInputElement).value = String(newWidth);

        setSize({ width: newWidth, height: v });
      } else {
        setSize({ ...size, height: v });
      }
    }
  };

  const isDisabledResize = useMemo(
    () => size.width < 100 || size.height < 100,
    [size]
  );

  const handleResize = () => {
    if (isDisabledResize) return;
    actions.changePageSize(size);
    setOpenResizeSetting(false);
  };
  return (
    <Fragment>
      <div
        css={(theme) => ({
          display: 'grid',
          alignItems: 'center',
          gridAutoFlow: 'column',
          gridGap: 8,
        })}
      >
        <SettingButton onClick={() => actions.setSidebar('LAYER_MANAGEMENT')}>
          <span
            css={(theme) => ({
              padding: '0 4px',
            })}
          >
            {t('page_editor.general.layer')}
          </span>
        </SettingButton>

        {selectedLayerIds.length > 0 &&
          !isLocked &&
          !isPageLocked &&
          (!isRootLayer(selectedLayers[0]) ||
            (isRootLayer(selectedLayers[0]) &&
              selectedLayers[0].data.props.image)) && (
            <Fragment>
              <div
                css={(theme) => ({
                  height: 32,
                  width: `1px`,
                  background: theme.colors.stroke,
                })}
              />
              <SettingButton
                ref={transparencyButtonRef}
                css={(theme) => ({ fontSize: 20 })}
                onClick={() => setOpenTransparencySetting(true)}
              >
                <TransparencyIcon
                  css={{
                    'html.dark &': {
                      filter: 'invert(100%)',
                    },
                  }}
                />
              </SettingButton>
              <Popover
                anchorEl={transparencyButtonRef.current}
                offsets={{
                  'bottom-end': { x: 0, y: 8 },
                }}
                open={openTransparencySetting}
                placement={breakpoint !== 'sm' ? 'bottom-start' : 'top-end'}
                onClose={() => setOpenTransparencySetting(false)}
              >
                <div
                  css={(theme) => ({
                    padding: 16,
                  })}
                >
                  <Slider
                    defaultValue={transparency * 100}
                    label={t('page_editor.general.transparency')}
                    onChange={updateTransparency}
                  />
                </div>
              </Popover>
            </Fragment>
          )}
        {!isPageLocked && (
          <Fragment>
            <div
              css={(theme) => ({
                height: 32,
                width: `1px`,
                background: theme.colors.stroke,
              })}
            />
            <SettingButton
              ref={resizeButtonRef}
              onClick={() => setOpenResizeSetting(true)}
            >
              <span
                css={(theme) => ({
                  padding: '0 4px',
                  fontSize: 14,
                })}
              >
                {t('page_editor.general.resize')}
              </span>
            </SettingButton>
          </Fragment>
        )}
        <Popover
          anchorEl={resizeButtonRef.current}
          offsets={{
            'bottom-end': { x: 0, y: 8 },
          }}
          open={openResizeSetting}
          placement={breakpoint !== 'sm' ? 'bottom-start' : 'top-end'}
          onClose={() => setOpenResizeSetting(false)}
        >
          <div
            css={(theme) => ({
              padding: 16,
              width: 240,
            })}
          >
            <div
              css={(theme) => ({
                display: 'flex',
                gap: 8,
                alignItems: 'flex-end',
              })}
            >
              <div>
                <div
                  css={(theme) => ({
                    fontSize: 12,
                    fontWeight: 600,
                    marginBottom: 4,
                  })}
                >
                  {t('page_editor.general.width')}
                </div>
                <div
                  css={(theme) => ({
                    border: `1px solid ${theme.colors.inputStroke}`,
                    height: 40,
                    width: 80,
                    borderRadius: 4,
                    overflow: 'hidden',
                  })}
                >
                  <input
                    ref={widthRef}
                    css={(theme) => ({
                      width: '100%',
                      padding: '0 12px',
                      minWidth: 8,
                      height: '100%',
                      background: theme.colors.input,
                    })}
                    defaultValue={size.width}
                    onChange={(e) => handleChangeSize(e.target.value, 'width')}
                  />
                </div>
              </div>
              <div>
                <div
                  css={(theme) => ({
                    fontSize: 12,
                    fontWeight: 600,
                    marginBottom: 4,
                  })}
                >
                  {t('page_editor.general.height')}
                </div>
                <div
                  css={(theme) => ({
                    border: `1px solid ${theme.colors.inputStroke}`,
                    height: 40,
                    width: 80,
                    borderRadius: 4,
                    overflow: 'hidden',
                  })}
                >
                  <input
                    ref={heightRef}
                    css={(theme) => ({
                      width: '100%',
                      padding: '0 12px',
                      minWidth: 8,
                      height: '100%',
                      background: theme.colors.input,
                    })}
                    defaultValue={size.height}
                    onChange={(e) => handleChangeSize(e.target.value, 'height')}
                  />
                </div>
              </div>
              <div
                css={(theme) => ({
                  fontSize: 20,
                  cursor: 'pointer',
                  margin: '10px 0',
                })}
                onClick={toggleLockAspectRatio}
              >
                {lockSiteAspect ? <LockKeyIcon /> : <LockKeyOpenIcon />}
              </div>
            </div>
            {isDisabledResize && (
              <div
                css={(theme) => ({
                  color: '#db1436',
                })}
              >
                Dimensions must be at least 100px and no more than 8000px.
              </div>
            )}
            <div
              css={(theme) => ({
                marginTop: 12,
              })}
            >
              <div
                css={(theme) => ({
                  background: theme.colors.background,
                  border: `1px solid ${theme.colors.stroke}`,
                  padding: '10px 14px',
                  lineHeight: 1,
                  borderRadius: 8,
                  cursor: !isDisabledResize ? 'pointer' : 'not-allowed',
                  fontSize: 14,
                  textAlign: 'center',
                  fontWeight: 600,
                  transition: 'all 0.2s',
                  ':hover': {
                    background: theme.colors.defaultButtonActive,
                  },
                })}
                onClick={handleResize}
              >
                {t('page_editor.general.resize')}
              </div>
            </div>
          </div>
        </Popover>
        {selectedLayerIds.length > 0 && (
          <SettingButton
            css={(theme) => ({ fontSize: 20 })}
            isActive={isLocked}
            onClick={toggleLock}
          >
            {isLocked && <LockKeyIcon />}
            {!isLocked && <LockKeyOpenIcon />}
          </SettingButton>
        )}
      </div>
      {sidebar === 'LAYER_MANAGEMENT' && <LayerSidebar open={true} />}
    </Fragment>
  );
};

export default CommonSettings;
